import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Header, { BreadcrumbItem } from '../components/Header';
import { usePageTracking } from '../hooks/usePageTracking';
import { meetingService } from '../services/meetingService';
import { useAuth } from '../context/AuthContext';
import JoinPreferencesModal from '../components/JoinPreferencesModal';
import styles from '../styles/joinMeetingByUrlPage.module.css';

interface JoinMeetingByUrlPageProps {
  user?: any;
  onLogout: () => void;
  onLogin: () => void;
  onCreateAccount?: () => void;
}

const JoinMeetingByUrlPage: React.FC<JoinMeetingByUrlPageProps> = ({
  user,
  onLogout,
  onLogin,
  onCreateAccount,
}) => {
  const { meetingCode, passPhrase } = useParams<{ meetingCode: string; passPhrase: string }>();
  const navigate = useNavigate();
  const { user: authUser } = useAuth();
  const [meetingName, setMeetingName] = useState<string | null>(null);
  const [resolvedCode, setResolvedCode] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const [joinPreferencesOpen, setJoinPreferencesOpen] = useState(false);

  const effectiveUser = user || authUser;

  usePageTracking('Join meeting by link');

  useEffect(() => {
    if (!meetingCode || !passPhrase) {
      setError('Invalid join link. Missing meeting code or pass phrase.');
      setLoading(false);
      return;
    }
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const meeting = await meetingService.getMeetingJoinByUrl(meetingCode, passPhrase);
        setMeetingName(meeting.name || '');
        setResolvedCode(meeting.meeting_code || meetingCode.trim().toUpperCase());
      } catch (err) {
        const message = err instanceof Error ? err.message : 'This link didn\'t work. The pass phrase may be wrong, or the link may be incorrect. Try the join link again or go to the meeting page and enter the pass phrase.';
        setError(message);
        setMeetingName(null);
        setResolvedCode(null);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [meetingCode, passPhrase]);

  const handleJoinMeeting = () => {
    setJoinPreferencesOpen(true);
  };

  const handleJoinWithPreferences = async (preferences: {
    display_preference?: string;
    include_on_printed_phone_list?: boolean;
    email_notifications_enabled?: boolean;
    sponsor_willingness?: string;
  }) => {
    if (!resolvedCode || !passPhrase || joining) return;
    setJoining(true);
    try {
      await meetingService.joinMeeting(resolvedCode, {
        ...preferences,
        join_key: passPhrase,
      });
      setJoinPreferencesOpen(false);
      navigate(`/meetings/${resolvedCode}`, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to join meeting');
    } finally {
      setJoining(false);
    }
  };

  const breadcrumbs: BreadcrumbItem[] = [
    { label: 'Home', path: '/', active: false },
    { label: 'Join meeting', path: '', active: true },
  ];

  if (loading) {
    return (
      <div className={styles.page}>
        <Header breadcrumbs={breadcrumbs} user={effectiveUser} onLogout={onLogout} onLogin={onLogin} />
        <main className={styles.main}>
          <p className={styles.loading}>Checking link…</p>
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.page}>
        <Header breadcrumbs={breadcrumbs} user={effectiveUser} onLogout={onLogout} onLogin={onLogin} />
        <main className={styles.main}>
          <div className={styles.card}>
            <h1 className={styles.title}>This link didn&apos;t work</h1>
            <p className={styles.errorMessage}>{error}</p>
            <p className={styles.hint}>
              Try the join link again, or go to the meeting page and enter the pass phrase.
            </p>
            <a href="/meetings" className={styles.link}>Find a meeting</a>
          </div>
        </main>
      </div>
    );
  }

  if (!effectiveUser) {
    return (
      <div className={styles.page}>
        <Header breadcrumbs={breadcrumbs} user={effectiveUser} onLogout={onLogout} onLogin={onLogin} />
        <main className={styles.main}>
          <div className={styles.card}>
            <h1 className={styles.title}>Join {meetingName || 'this meeting'}?</h1>
            <p className={styles.prompt}>Sign in or create an account to join this meeting.</p>
            <div className={styles.actions}>
              <button type="button" onClick={onLogin} className={styles.primaryButton}>
                Sign in
              </button>
              <button type="button" onClick={onCreateAccount ?? onLogin} className={styles.secondaryButton}>
                Create account
              </button>
            </div>
            <p className={styles.privacy}>
              Only share this link with people in the meeting. Close this tab after joining if you&apos;re on a shared device.
            </p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <Header breadcrumbs={breadcrumbs} user={effectiveUser} onLogout={onLogout} onLogin={onLogin} />
      <main className={styles.main}>
        <div className={styles.card}>
          <h1 className={styles.title}>Join {meetingName || 'this meeting'}?</h1>
          <p className={styles.description}>
            You&apos;ll be added to this meeting&apos;s member list and can access the meeting home page.
          </p>
          <div className={styles.actions}>
            <button type="button" onClick={handleJoinMeeting} disabled={joining} className={styles.primaryButton}>
              {joining ? 'Adding…' : 'Join meeting'}
            </button>
            <a href="/meetings" className={styles.cancelLink}>Cancel</a>
          </div>
          <p className={styles.privacy}>
            Only share this link with people in the meeting. Close this tab after joining if you&apos;re on a shared device.
          </p>
        </div>

        <JoinPreferencesModal
          isOpen={joinPreferencesOpen}
          onClose={() => setJoinPreferencesOpen(false)}
          onSubmit={handleJoinWithPreferences}
          isSubmitting={joining}
          meetingName={meetingName ?? undefined}
        />
      </main>
    </div>
  );
};

export default JoinMeetingByUrlPage;
